import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, StatCard, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, DownloadSimple, Eye, ArrowClockwise, Receipt as ReceiptIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const MODE_LABEL = { cash: "Cash", cheque: "Cheque", upi: "UPI", bank_transfer: "Bank Transfer", online: "Online", other: "Other" };

export default function Receipts() {
    const [receipts, setReceipts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState({ open: false, receipt: null });
    const [filters, setFilters] = useState({ q: "", mode: "", block: "", date_from: "", date_to: "" });
    const [backfilling, setBackfilling] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [rRes, sRes, fRes] = await Promise.all([
                api.get("/receipts", { params: cleanParams(filters) }),
                api.get("/receipts/summary"),
                api.get("/flats"),
            ]);
            setReceipts(rRes.data);
            setSummary(sRes.data);
            setFlats(fRes.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);

    const blocks = useMemo(() => Array.from(new Set(flats.map(f => f.block))).sort(), [flats]);

    async function downloadPdf(r) {
        try {
            const resp = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${r.receipt_number}-${r.flat_label || r.flat_id.slice(0, 6)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function backfill() {
        if (!window.confirm("Issue receipts for any fully-paid invoices that don't have one yet? This is idempotent — safe to run.")) return;
        setBackfilling(true);
        try {
            const res = await api.post("/receipts/backfill");
            toast.success(`Backfill complete — ${res.data.issued} new receipts issued (${res.data.already_had_receipt} already existed)`);
            load();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBackfilling(false); }
    }

    return (
        <div data-testid="receipts-page">
            <PageHeader
                overline="Finance"
                title="Receipts"
                description="Payment receipts generated for society invoices."
                actions={
                    <>
                        {summary && summary.pending > 0 && (
                            <Button variant="outline" className="rounded-full" onClick={backfill} disabled={backfilling} data-testid="btn-backfill">
                                <ArrowClockwise size={14} className="mr-1" /> {backfilling ? "Issuing…" : `Issue ${summary.pending} pending`}
                            </Button>
                        )}
                    </>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <StatCard label="Total receipts" value={summary?.total ?? "—"} hint="All time" />
                <StatCard label="This month" value={summary?.this_month ?? "—"} hint="Issued in current month" accent />
                <StatCard label="Total received" value={summary ? inr(summary.total_amount) : "—"} hint="Represented by receipts" />
                <StatCard label="Pending receipts" value={summary?.pending ?? "—"} hint="Fully paid invoices awaiting a receipt" />
            </div>

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-3 mb-4 flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Search</Label>
                    <div className="relative mt-1">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="rcpt-q" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} placeholder="Receipt no. / flat / resident / invoice" className="pl-7 rounded-sm border-brand-line h-9" />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Mode</Label>
                    <Select value={filters.mode || "any"} onValueChange={(v) => setFilters({ ...filters, mode: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-36" data-testid="rcpt-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any mode</SelectItem>
                            {Object.entries(MODE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Block</Label>
                    <Select value={filters.block || "any"} onValueChange={(v) => setFilters({ ...filters, block: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-28" data-testid="rcpt-block"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">All blocks</SelectItem>
                            {blocks.map(b => <SelectItem key={b} value={b}>Block {b}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">From</Label>
                    <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="rounded-sm border-brand-line h-9 mt-1" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">To</Label>
                    <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="rounded-sm border-brand-line h-9 mt-1" />
                </div>
                {(filters.q || filters.mode || filters.block || filters.date_from || filters.date_to) && (
                    <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => setFilters({ q: "", mode: "", block: "", date_from: "", date_to: "" })}>Clear</Button>
                )}
            </div>

            {loading ? <TableLoadingSkeleton rows={5} /> : receipts.length === 0 ? (
                <EmptyState
                    title="No receipts to show"
                    description="Receipts are issued automatically the moment an invoice is fully paid. You can also backfill receipts for legacy paid invoices."
                    action={summary && summary.pending > 0 ? (
                        <Button className="rounded-full" onClick={backfill} disabled={backfilling}>
                            <ArrowClockwise size={14} className="mr-1" /> Issue {summary.pending} pending receipts
                        </Button>
                    ) : null}
                />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="receipts-table">
                        <thead className="bg-brand-bg">
                            <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                <th className="px-3 py-2 text-left">Receipt No.</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Flat</th>
                                <th className="px-3 py-2 text-left">Resident</th>
                                <th className="px-3 py-2 text-left">Invoice No.</th>
                                <th className="px-3 py-2 text-left">Period</th>
                                <th className="px-3 py-2 text-left">Mode</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {receipts.map(r => (
                                <tr key={r.id} className="border-t border-brand-line hover:bg-brand-bg/40" data-testid={`receipt-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs text-brand-ink">{r.receipt_number}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{(r.created_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 font-medium">{r.flat_label || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{r.resident_name || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-brand-ink" data-testid={`receipt-invoice-ref-${r.id}`}>{r.invoice_ref || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{r.invoice_month || r.invoice_description || "—"}</td>
                                    <td className="px-3 py-2">{MODE_LABEL[r.payment_mode] || "—"}</td>
                                    <td className="px-3 py-2 text-right font-medium">{inrFull(r.amount)}</td>
                                    <td className="px-3 py-2"><Chip variant="success">{r.status}</Chip></td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button onClick={() => setPreview({ open: true, receipt: r })} className="p-1.5 text-brand-inkSoft hover:text-brand-action" title="View" data-testid={`btn-view-${r.id}`}><Eye size={14} /></button>
                                        <button onClick={() => downloadPdf(r)} className="p-1.5 text-brand-inkSoft hover:text-brand-action" title="Download PDF" data-testid={`btn-pdf-${r.id}`}><DownloadSimple size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ReceiptPreview
                open={preview.open}
                receipt={preview.receipt}
                onClose={() => setPreview({ open: false, receipt: null })}
                onDownload={downloadPdf}
            />
        </div>
    );
}

function cleanParams(o) {
    const out = {};
    for (const k of Object.keys(o)) if (o[k]) out[k] = o[k];
    return out;
}

function ReceiptPreview({ open, receipt, onClose, onDownload }) {
    if (!receipt) return null;
    const snap = receipt.snapshot || {};
    const charges = receipt.invoice_charges || [];
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="receipt-preview">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Payment receipt</DialogTitle>
                    <DialogDescription>{receipt.receipt_number}</DialogDescription>
                </DialogHeader>
                <div className="bg-white border border-brand-line rounded-sm p-6 space-y-5">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Payment Receipt</p>
                            <p className="font-heading text-xl text-brand-action mt-1">{receipt.receipt_number}</p>
                            <p className="text-xs text-brand-inkSoft mt-1">Issued {(receipt.created_at || "").slice(0, 10)}</p>
                        </div>
                        <Chip variant="success">PAID</Chip>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Received from" value={snap.resident_name || "—"} />
                        <Field label="Flat" value={receipt.flat_label || snap.flat_label || "—"} />
                        <Field label="Invoice No." value={receipt.invoice_ref || "—"} />
                        <Field label="Invoice period" value={receipt.invoice_month || receipt.invoice_description || "—"} />
                        <Field label="Payment date" value={receipt.payment_date || "—"} />
                        <Field label="Payment mode" value={MODE_LABEL[receipt.payment_mode] || "—"} />
                        <Field label="Amount received" value={inrFull(receipt.amount)} strong />
                    </div>
                    {receipt.transaction_reference && (
                        <div className="text-sm border-t border-brand-line pt-3">
                            <span className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Reference</span>
                            <div className="text-brand-ink">{receipt.transaction_reference}</div>
                        </div>
                    )}
                    {snap.cheque_number && (
                        <div className="text-sm">
                            <span className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Cheque</span>
                            <div className="text-brand-ink">#{snap.cheque_number}{snap.cheque_date ? ` · ${snap.cheque_date}` : ""}{snap.bank_name ? ` · ${snap.bank_name}` : ""}</div>
                        </div>
                    )}
                    {charges.length > 0 && (
                        <div className="border-t border-brand-line pt-3">
                            <span className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Charges</span>
                            <table className="w-full text-sm mt-1">
                                <tbody>
                                    {charges.map((c, i) => (
                                        <tr key={i} className="border-t border-brand-line/60">
                                            <td className="py-1.5">{c.name}</td>
                                            <td className="py-1.5 text-right">{inrFull(c.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="flex justify-between items-center bg-[#33383F] text-white rounded-sm px-4 py-3">
                        <span className="text-sm">Total received</span>
                        <span className="font-heading text-lg">{inrFull(receipt.amount)}</span>
                    </div>
                    <p className="text-[11px] text-brand-inkSoft text-center">This is a system-generated payment receipt from Maintayn — Community OS.</p>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={onClose}>Close</Button>
                    <Button className="rounded-full" onClick={() => onDownload(receipt)} data-testid="preview-download">
                        <DownloadSimple size={14} className="mr-1" /> Download PDF
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value, strong }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label}</p>
            <p className={`text-sm ${strong ? "font-heading text-lg text-brand-ink" : "text-brand-ink"}`}>{value}</p>
        </div>
    );
}
